export interface ReputationScores {
  reviewManagement: number;
  onlineMonitoring: number;
  brandTrust: number;
}

export interface ReputationResult {
  questionId: number;
  selectedOption: number;
  scores: ReputationScores;
}

export interface Question {
  id: number;
  question: string;
  options: {
    text: string;
    scores: ReputationScores;
  }[];
}

export const questions: Question[] = [
  {
    id: 1,
    question: "How many online reviews does your Saskatchewan business currently have?",
    options: [
      {
        text: "Substantial - 50+ reviews across multiple platforms with consistent flow",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Moderate - 10-50 reviews, mostly on one or two platforms",
        scores: { reviewManagement: 2, onlineMonitoring: 1, brandTrust: 2 }
      },
      {
        text: "Minimal - under 10 reviews scattered across platforms",
        scores: { reviewManagement: 3, onlineMonitoring: 2, brandTrust: 3 }
      },
      {
        text: "None or very few - we rarely receive reviews",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  },
  {
    id: 2,
    question: "Do you actively request reviews from satisfied customers?",
    options: [
      {
        text: "Yes - systematic process to ask every customer for reviews",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Sometimes - we ask occasionally but no formal system",
        scores: { reviewManagement: 2, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "Rarely - only when we remember or after exceptional service",
        scores: { reviewManagement: 3, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "No - we wait for customers to leave reviews on their own",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  },
  {
    id: 3,
    question: "How do you respond to online reviews (both positive and negative)?",
    options: [
      {
        text: "Professionally and promptly - respond to all reviews within 24-48 hours",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Selectively - respond to negative reviews but not all positive ones",
        scores: { reviewManagement: 2, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "Rarely - only respond when absolutely necessary",
        scores: { reviewManagement: 3, onlineMonitoring: 2, brandTrust: 3 }
      },
      {
        text: "Never - we don't respond to reviews",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  },
  {
    id: 4,
    question: "Are you monitoring what people say about your business online?",
    options: [
      {
        text: "Actively - use tools to track mentions, reviews, and social media conversations",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Occasionally - check review sites and Google sometimes",
        scores: { reviewManagement: 2, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "Rarely - only find out when someone tells us about it",
        scores: { reviewManagement: 2, onlineMonitoring: 3, brandTrust: 2 }
      },
      {
        text: "Not at all - we don't monitor online mentions",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  },
  {
    id: 5,
    question: "What is your average star rating across review platforms?",
    options: [
      {
        text: "Excellent - 4.5+ stars with consistent positive feedback",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Good - 4.0-4.4 stars with mostly positive reviews",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 2 }
      },
      {
        text: "Fair - 3.0-3.9 stars with mixed reviews",
        scores: { reviewManagement: 2, onlineMonitoring: 2, brandTrust: 3 }
      },
      {
        text: "Poor or unknown - below 3 stars or we don't track ratings",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  },
  {
    id: 6,
    question: "How do you handle negative reviews or online criticism?",
    options: [
      {
        text: "Strategic approach - respond professionally, resolve issues, learn from feedback",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Basic response - address complaints but no systematic improvement process",
        scores: { reviewManagement: 2, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "Defensively - respond but sometimes emotionally or argumentatively",
        scores: { reviewManagement: 3, onlineMonitoring: 2, brandTrust: 3 }
      },
      {
        text: "Ignore them - hope negative reviews go away or get buried",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  },
  {
    id: 7,
    question: "How much do you think your online reputation impacts new customer decisions?",
    options: [
      {
        text: "Critical - we know reviews directly influence most purchasing decisions",
        scores: { reviewManagement: 1, onlineMonitoring: 1, brandTrust: 1 }
      },
      {
        text: "Important - reviews matter but we compete on other factors too",
        scores: { reviewManagement: 1, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "Somewhat - some customers check reviews but not all",
        scores: { reviewManagement: 2, onlineMonitoring: 2, brandTrust: 2 }
      },
      {
        text: "Minimal - we don't think reviews impact our business much",
        scores: { reviewManagement: 3, onlineMonitoring: 3, brandTrust: 3 }
      }
    ]
  }
];
